import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { captureMutationError } from '@/lib/sentry';
import {
  CreateInspectionRequest,
  ApproveInspectionRequest,
  LandInspection,
  PaginatedResponse,
} from '@property/shared';
import { toast, showMutationErrorToast } from '@/lib/toast';

export interface CreateInspectionResponse {
  success: boolean;
  inspectionId: string;
}

export interface CreateInspectionVariables {
  request: CreateInspectionRequest;
  taskId: string;
  propertyId: string;
}

export function useCreateInspection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ request }: CreateInspectionVariables) => {
      return api.submitInspectionReport(request);
    },

    onSuccess: (_result, variables) => {
      const { taskId, propertyId } = variables;
      queryClient.invalidateQueries({ queryKey: queryKeys.inspections.byProperty(propertyId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.inspections.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.detail(taskId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all() });
      toast.success('Inspection report submitted.');
    },

    onError: (error, { taskId, propertyId }) => {
      captureMutationError(error, { mutation: 'useCreateInspection', taskId, propertyId });
      showMutationErrorToast(error, 'Failed to submit inspection. Please try again.');
    },
  });
}

interface ReviewMutationVars {
  id: string;
  request: ApproveInspectionRequest;
}

function useReviewMutation(
  apiFn: (id: string, request: ApproveInspectionRequest) => Promise<LandInspection>,
  successMessage: string,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, request }: ReviewMutationVars) => apiFn(id, request),

    onMutate: async ({ id }: ReviewMutationVars) => {
      // Cancel in-flight queries for the whole inspections namespace
      await queryClient.cancelQueries({ queryKey: queryKeys.inspections.all() });

      // Snapshot all cached entries for rollback
      const snapshot = queryClient.getQueriesData<PaginatedResponse<LandInspection>>({
        queryKey: queryKeys.inspections.all(),
      });

      // Optimistically splice the item out of every cached paginated batch.
      // Guard against non-paginated cache entries (plain arrays) that don't have .data.
      queryClient.setQueriesData<PaginatedResponse<LandInspection>>(
        { queryKey: queryKeys.inspections.all() },
        (old) =>
          old && Array.isArray(old.data)
            ? { ...old, data: old.data.filter((i) => i.inspectionId !== id) }
            : old,
      );

      return { snapshot };
    },

    onSuccess: () => {
      toast.success(successMessage);
    },

    onError: (_err, { id }, context) => {
      captureMutationError(_err, { mutation: 'useReviewMutation', inspectionId: id });
      // Restore all snapshotted batches on failure
      context?.snapshot.forEach(([key, val]) => queryClient.setQueryData(key, val));
      showMutationErrorToast(_err, 'Something went wrong. Please try again.');
    },

    onSettled: (_result, _err, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inspections.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.inspections.detail(id) });
    },
  });
}

export function useApproveInspection() {
  return useReviewMutation(api.approveInspection, 'Inspection approved.');
}

export function useRejectInspection() {
  return useReviewMutation(api.rejectInspection, 'Inspection rejected.');
}
